import type { ReactElement } from "react";
import { Link } from "@tanstack/react-router";
import { FiGlobe, FiUpload } from "react-icons/fi";
import type { ProjectSource } from "../lib/useProject";

function YouTubeIcon({ className }: { className?: string }): ReactElement {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" className={className}>
			<path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.55A3.02 3.02 0 0 0 .5 6.19 31.6 31.6 0 0 0 0 12a31.6 31.6 0 0 0 .5 5.81 3.02 3.02 0 0 0 2.12 2.14c1.88.55 9.38.55 9.38.55s7.5 0 9.38-.55a3.02 3.02 0 0 0 2.12-2.14A31.6 31.6 0 0 0 24 12a31.6 31.6 0 0 0-.5-5.81zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" />
		</svg>
	);
}

function TypeIcon({
	itemType,
	className,
}: {
	itemType: string;
	className?: string;
}): ReactElement {
	if (itemType === "youtube_video")
		return <YouTubeIcon className={className} />;
	if (itemType === "upload") return <FiUpload className={className} />;
	return <FiGlobe className={className} />;
}

function formatDomain(url: string): string {
	try {
		return new URL(url).hostname.replace(/^www\./, "");
	} catch {
		return url;
	}
}

export function SourceCard({
	source,
}: {
	source: ProjectSource;
}): ReactElement {
	const title =
		source.title ??
		source.contents?.title ??
		source.contents?.canonical_url ??
		"Untitled";
	const domain = source.contents?.canonical_url
		? formatDomain(source.contents.canonical_url)
		: null;
	const imageUrl = source.contents?.og_image_url ?? null;
	const channelName = source.contents?.channel_name ?? null;

	const linkProps = source.content_id
		? {
				to: "/content/$contentId" as const,
				params: { contentId: source.content_id },
				search: { highlight: undefined, chunkIndex: undefined },
			}
		: null;

	const cardContent = (
		<>
			{imageUrl && (
				<div className="bg-skeleton aspect-video w-full overflow-hidden rounded-t-lg">
					<img
						src={imageUrl}
						alt=""
						className="h-full w-full object-cover"
						loading="lazy"
					/>
				</div>
			)}
			<div
				className={`flex flex-col gap-1.5 ${imageUrl ? "p-3" : "p-3"}`}
			>
				<div className="flex items-start gap-2">
					{!imageUrl && (
						<TypeIcon
							itemType={source.item_type}
							className="text-fg-ghost mt-0.5 h-3.5 w-3.5 shrink-0"
						/>
					)}
					<span className="text-fg-secondary line-clamp-2 min-w-0 text-sm leading-snug">
						{title}
					</span>
				</div>
				<div className="text-fg-ghost flex items-center gap-2 text-[11px]">
					{channelName && (
						<span className="truncate">{channelName}</span>
					)}
					{channelName && domain && <span>&middot;</span>}
					{domain && <span className="truncate">{domain}</span>}
				</div>
			</div>
		</>
	);

	const className =
		"border-line bg-surface hover:border-line-strong block overflow-hidden rounded-lg border shadow-sm transition-colors";

	if (linkProps) {
		return (
			<Link {...linkProps} className={className}>
				{cardContent}
			</Link>
		);
	}

	return <div className={className}>{cardContent}</div>;
}
